import React, { useState } from 'react';
import axios from 'axios';
import { X, Loader2 } from 'lucide-react';
import { API, formatErr, Field, inputCls, todayStr } from './shared';

const StockDialog = ({ item, mode, onClose, onSaved, authHeader }) => {
  const [qty, setQty] = useState('');
  const [notes, setNotes] = useState('');
  const [carModel, setCarModel] = useState('');
  const [transactionDate, setTransactionDate] = useState(todayStr());
  const [unitCost, setUnitCost] = useState('');
  const [err, setErr] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const isConsume = mode === 'consume';

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setSubmitting(true);
    try {
      const path = isConsume ? 'consume' : 'restock';
      const body = { quantity: Number(qty), notes, transaction_date: transactionDate, car_model: carModel };
      // Only attach unit_cost on restock and only if non-empty
      if (!isConsume && unitCost !== '' && unitCost !== null && unitCost !== undefined) {
        const parsed = Number(unitCost);
        if (!Number.isNaN(parsed) && parsed > 0) body.unit_cost = parsed;
      }
      await axios.post(
        `${API}/inventory/${item.id}/${path}`,
        body,
        { headers: authHeader() }
      );
      onSaved();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-lg font-semibold" data-testid="stock-dialog-title">
            {isConsume ? 'Record Consumption' : 'Restock Item'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="stock-dialog-close"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <div className="bg-gradient-to-br from-teal-500/15 to-slate-800/60 border border-teal-400/30 rounded-lg px-4 py-3 shadow-inner">
            <div className="font-semibold text-white text-base" data-testid="stock-dialog-item-name">{item.name}</div>
            <div className="text-xs text-slate-300 mt-1.5">
              Current: <span className="text-teal-200 font-semibold">{item.quantity} {item.unit}</span>
              <span className="mx-2 text-slate-500">·</span>
              Safety: <span className="text-slate-200 font-medium">{item.safety_stock} {item.unit}</span>
            </div>
          </div>
          <Field label={`Quantity to ${isConsume ? 'consume' : 'add'} (${item.unit})`} required>
            <input
              type="number"
              required
              min="0.01"
              step="0.01"
              max={isConsume ? item.quantity : undefined}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className={inputCls}
              autoFocus
              data-testid="stock-qty-input"
            />
          </Field>
          <Field label={isConsume ? 'Consumption date' : 'Restock date'}>
            <input
              type="date"
              value={transactionDate}
              max={todayStr()}
              onChange={(e) => setTransactionDate(e.target.value)}
              className={inputCls}
              data-testid="stock-date-input"
            />
            <p className="text-xs text-slate-500 mt-1">
              Defaults to today — change this to record a {isConsume ? 'past consumption' : 'past restock'}.
            </p>
          </Field>
          {isConsume && (
            <Field label="Car model (vehicle the material was used on)">
              <input
                value={carModel}
                onChange={(e) => setCarModel(e.target.value)}
                className={inputCls}
                placeholder="e.g. Honda City, Hyundai Creta, BMW X5"
                data-testid="stock-car-model-input"
              />
            </Field>
          )}
          <Field label="Notes (optional)">
            <input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={inputCls}
              placeholder={isConsume ? 'e.g. Job #1234, Honda City' : 'e.g. PO #4521 received'}
              data-testid="stock-notes-input"
            />
          </Field>
          {err && <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded px-3 py-2" data-testid="stock-error">{err}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg" data-testid="stock-cancel">Cancel</button>
            <button
              type="submit"
              disabled={submitting}
              className={`px-4 py-2 text-sm font-semibold rounded-lg flex items-center gap-2 ${
                isConsume
                  ? 'bg-orange-500 hover:bg-orange-400 disabled:bg-orange-700 text-slate-950'
                  : 'bg-emerald-500 hover:bg-emerald-400 disabled:bg-emerald-700 text-slate-950'
              }`}
              data-testid="stock-submit"
            >
              {submitting && <Loader2 className="animate-spin" size={14} />}
              {isConsume ? 'Record Consumption' : 'Add to Stock'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StockDialog;
